import { MatterDto, MatterForm, MatterStateType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { PracticeArea, PracticeAreaType } from 'types/practice-areas';

/**
 * The practice areas a new case takes over from the case it is prefilled from, as the form's three fields.
 * While a case takes one practice area, only the source's first top-level area comes along; each other one is
 * left behind with what `useMatterForm`'s `handlePracticeAreaChange` drops when an area is removed: its
 * sub-areas, and the area for DMS when that is the area or sits under it.
 */
export const copiedPracticeAreas = (
    source: Pick<MatterDto, 'practiceAreas' | 'dmsPracticeArea'>,
    isSinglePracticeArea: boolean
): Pick<MatterForm, 'practiceAreas' | 'secondLevelPracticeAreas' | 'dmsPracticeArea'> => {
    const named = (area: PracticeArea): PracticeArea => ({ ...area, name: area.fullPath });
    const ofType = (type: PracticeAreaType): PracticeArea[] =>
        source.practiceAreas?.filter((area) => area.practiceAreaTypeCode === type).map(named) ?? [];
    const topLevel = ofType(PracticeAreaType.PRACTICE_AREA);
    const leftBehind = isSinglePracticeArea ? topLevel.filter((area) => area.id !== topLevel[0].id) : [];
    const isLeftBehind = (area: PracticeArea): boolean =>
        leftBehind.some((parent) => parent.id === area.id || parent.id === area.parentPracticeArea?.id);
    const dmsPracticeArea = source.dmsPracticeArea ? named(source.dmsPracticeArea) : null;
    return {
        practiceAreas: isSinglePracticeArea ? topLevel.slice(0, 1) : topLevel,
        secondLevelPracticeAreas: ofType(PracticeAreaType.PRACTICE_SUBAREA).filter((area) => !isLeftBehind(area)),
        dmsPracticeArea: dmsPracticeArea && isLeftBehind(dmsPracticeArea) ? null : dmsPracticeArea,
    };
};

/**
 * The case's practice areas once a sub-area is picked: its parent joins them when it is not among them. While a
 * case takes one practice area the parent only joins an empty list, and a sub-area of any other area is refused
 * (`null`): the open list of options, loaded before an area was chosen, still offers it.
 */
export const practiceAreasWithParentOf = (
    subArea: PracticeArea,
    practiceAreas: PracticeArea[],
    isSinglePracticeArea: boolean
): PracticeArea[] | null => {
    const parent = subArea.parentPracticeArea;
    if (!parent || practiceAreas.some((area) => area.id === parent.id)) {
        return practiceAreas;
    }
    if (isSinglePracticeArea && practiceAreas.length > 0) {
        return null;
    }
    return [
        ...practiceAreas,
        {
            id: parent.id,
            name: parent.name,
            taxonomyId: parent.taxonomyId,
            isActive: true,
            practiceAreaTypeCode: PracticeAreaType.PRACTICE_AREA,
            fullPath: parent.name,
            isChildAllowed: true,
            isAmlApplicable: false,
            parentPracticeArea: null,
        },
    ];
};

/**
 * Whether the save being prepared is the one that moves a Case into Offer Won — the only save that offers
 * to open the resulting Case straight away. Re-saving a Case that already is Offer Won is not that save;
 * the kebab action covers it from then on.
 */
export const isStatusChangingToOfferWon = (
    isEditMode: boolean,
    selectedStatusCode: string | undefined,
    persistedStatusCode: string | undefined
): boolean =>
    isEditMode && selectedStatusCode === MatterStateType.OFFER_WON && persistedStatusCode !== MatterStateType.OFFER_WON;

// Map and filter parties based on type
export const reduceParties = (
    parties:
        | {
              party: NamedEntityMinDto;
              matterPartyRoleTypeCode: string;
          }[]
        | null,
    matterPartyRoleTypeCode: string
): NamedEntityMinDto[] => {
    const contacts = parties?.reduce<NamedEntityMinDto[]>((acc, val) => {
        if (val.matterPartyRoleTypeCode === matterPartyRoleTypeCode && val.party.name) {
            // Carry first/last through so the selected chip can flip to "Last First" (LAST_NAME_FIRST).
            acc.push({
                id: val.party.id,
                name: val.party.name,
                firstName: val.party.firstName,
                lastName: val.party.lastName,
            });
        }
        return acc;
    }, []);
    return contacts ?? [];
};

/**
 * Splits the case's STAFF links into the two form fields. With the feature off there is no Case
 * assistant field to hold the marked links, so they stay in Handled by — anything else would load a
 * person the case view shows under Handled by into a hidden field the user cannot see but the form
 * still submits.
 */
export const splitCaseStaff = (
    employeesIn: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[],
    isAdministratorFieldEnabled: boolean
): { employees: NamedEntityMinDto[]; administrators: NamedEntityMinDto[] } => {
    if (!isAdministratorFieldEnabled) {
        return { employees: reduceStaff(employeesIn), administrators: [] };
    }
    return {
        employees: reduceEmployees(employeesIn, 'STAFF'),
        administrators: reduceEmployees(employeesIn, 'STAFF', true),
    };
};

/** Every STAFF link regardless of the administrator marker. */
const reduceStaff = (
    employeesIn: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[]
): NamedEntityMinDto[] => [...reduceEmployees(employeesIn, 'STAFF'), ...reduceEmployees(employeesIn, 'STAFF', true)];

/**
 * Map and filter employees based on type. `administrator` splits the STAFF links: `false` is the
 * case's Handled by list and `true` its case assistants, so a caller asking for STAFF must say which
 * — passing neither would put the assistants in the Handled by field.
 */
export const reduceEmployees = (
    employeesIn: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[],
    matterEmployeeRoleTypeCode: string,
    administrator = false
): NamedEntityMinDto[] => {
    const employees = employeesIn?.reduce<NamedEntityMinDto[]>((acc, val) => {
        if (
            val.matterEmployeeRoleTypeCode === matterEmployeeRoleTypeCode &&
            !!val.administrator === administrator &&
            val.employee.name
        ) {
            // Carry first/last through so the selected chip can flip to "Last First" (LAST_NAME_FIRST).
            acc.push({
                id: val.employee.id,
                name: val.employee.name,
                firstName: val.employee.firstName,
                lastName: val.employee.lastName,
            });
        }
        return acc;
    }, []);
    return employees ?? [];
};
